import { LoadingCommon } from '@components';
import { Button, Form, Input, Radio, Select } from 'antd';
import { formatAssetNo } from '@utils/helpers';
import { SaveConfirmDialog } from './components/SaveConfirmDialog';
import {
  FIXED_ASSET_DETAIL_CONSTANT,
  SELECTABLE_STATUSES,
  TAssetDetail,
  TAssetStatus,
} from './constant';
import { DetailWrapper, StatusBadge } from './FixedAssetLedgerDetailStyled';
import useFixedAssetLedgerDetail from './hooks/useFixedAssetLedgerDetail';

const { TITLE, LABEL, NAME } = FIXED_ASSET_DETAIL_CONSTANT;

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }
  return value.toLocaleString('ja-JP');
}

/** Returns antd Form.Item validateStatus for a field, given the current inline error map. */
function getValidateStatus(
  validationErrors: Record<string, string>,
  name: string
): '' | 'error' {
  return validationErrors[name] ? 'error' : '';
}

/** [4-13] READ-ONLY section — extracted to keep the main component's complexity low. */
function ReadOnlySection({
  asset,
  onNavigateToPhoto,
}: {
  asset: TAssetDetail | null;
  onNavigateToPhoto: () => void;
}) {
  return (
    <div className="detail_section">
      <div className="detail_section-title">{TITLE.READONLY_SECTION}</div>
      <div className="detail_readonly-grid">
        {/* [4] 資産番号 */}
        <div className="detail_readonly-item">
          <span className="detail_readonly-label">{LABEL.ASSET_NO}</span>
          <span className="detail_readonly-value">
            {formatAssetNo(asset?.assetNo) || '—'}
          </span>
        </div>

        {/* [5] 会社 */}
        <div className="detail_readonly-item">
          <span className="detail_readonly-label">{LABEL.COMPANY}</span>
          <span className="detail_readonly-value">{asset?.company || '—'}</span>
        </div>

        {/* [6] 店舗 */}
        <div className="detail_readonly-item">
          <span className="detail_readonly-label">{LABEL.STORE}</span>
          <span className="detail_readonly-value">{asset?.store || '—'}</span>
        </div>

        {/* [7] 種類 */}
        <div className="detail_readonly-item">
          <span className="detail_readonly-label">{LABEL.ASSET_TYPE}</span>
          <span className="detail_readonly-value">
            {asset?.assetType || '—'}
          </span>
        </div>

        {/* [8] 取得年月日 */}
        <div className="detail_readonly-item">
          <span className="detail_readonly-label">
            {LABEL.ACQUISITION_DATE}
          </span>
          <span className="detail_readonly-value">
            {asset?.acquisitionDate || '—'}
          </span>
        </div>

        {/* [9] 耐用年数 */}
        <div className="detail_readonly-item">
          <span className="detail_readonly-label">{LABEL.USEFUL_LIFE}</span>
          <span className="detail_readonly-value">
            {asset?.usefulLife != null ? `${asset.usefulLife} 年` : '—'}
          </span>
        </div>

        {/* [10] 取得価額 */}
        <div className="detail_readonly-item">
          <span className="detail_readonly-label">
            {LABEL.ACQUISITION_COST}
          </span>
          <span className="detail_readonly-value">
            {formatCurrency(asset?.acquisitionCost)}
          </span>
        </div>

        {/* [11] 期首帳簿価額 */}
        <div className="detail_readonly-item">
          <span className="detail_readonly-label">
            {LABEL.BOOK_VALUE_START}
          </span>
          <span className="detail_readonly-value">
            {formatCurrency(asset?.bookValueStart)}
          </span>
        </div>

        {/* [12] 最新年月日 */}
        <div className="detail_readonly-item">
          <span className="detail_readonly-label">{LABEL.UPDATED_AT}</span>
          <span className="detail_readonly-value">
            {asset?.updatedAt || '—'}
          </span>
        </div>

        {/* [13] 写真有無 */}
        <div className="detail_readonly-item">
          <span className="detail_readonly-label">{LABEL.HAS_PHOTO}</span>
          {asset?.hasPhoto ? (
            <span
              className="detail_readonly-value is-link"
              onClick={onNavigateToPhoto}
            >
              {FIXED_ASSET_DETAIL_CONSTANT.PHOTO.EXISTS}
            </span>
          ) : (
            <span className="detail_readonly-value is-empty">
              {FIXED_ASSET_DETAIL_CONSTANT.PHOTO.NONE}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function FixedAssetLedgerDetail() {
  const { state, handler } = useFixedAssetLedgerDetail();
  const {
    asset,
    loadingPage,
    loadingSave,
    showSaveDialog,
    targetStatus,
    validationErrors,
    isStatusLocked,
    form,
    departmentOptions,
    locationOptions,
  } = state;

  return (
    <LoadingCommon loading={loadingPage}>
      <DetailWrapper>
        {/* [3] Header: back button left, title centered */}
        <div className="detail_header">
          <button className="detail_back-btn" onClick={handler.handleBack}>
            {TITLE.BTN_BACK}
          </button>
          <h1>{TITLE.PAGE}</h1>
        </div>

        {/* [4-13] READ-ONLY section */}
        <ReadOnlySection
          asset={asset}
          onNavigateToPhoto={handler.handleNavigateToPhoto}
        />

        {/* [14-19] EDITABLE section */}
        <div className="detail_section">
          <div className="detail_section-title">{TITLE.EDITABLE_SECTION}</div>
          <Form form={form} layout="vertical">
            <div className="detail_form-grid">
              {/* [14] 資産名称 (required) */}
              <div className="detail_form-full">
                <Form.Item
                  label={LABEL.ASSET_NAME}
                  name={NAME.ASSET_NAME}
                  validateStatus={getValidateStatus(
                    validationErrors,
                    NAME.ASSET_NAME
                  )}
                  help={validationErrors[NAME.ASSET_NAME]}
                >
                  <Input
                    size="large"
                    maxLength={100}
                    placeholder={LABEL.ASSET_NAME_PLACEHOLDER}
                  />
                </Form.Item>
              </div>

              {/* [15] 部門 — options from GET /api/v1/assets/lookups (fallback: MOCK_DEPARTMENTS) */}
              <Form.Item label={LABEL.DEPARTMENT} name={NAME.DEPARTMENT}>
                <Select
                  size="large"
                  allowClear
                  options={departmentOptions}
                  placeholder={LABEL.DEPARTMENT}
                />
              </Form.Item>

              {/* [16] 所属 — options from GET /api/v1/assets/lookups (fallback: MOCK_LOCATIONS) */}
              <Form.Item label={LABEL.LOCATION} name={NAME.LOCATION}>
                <Select
                  size="large"
                  allowClear
                  options={locationOptions}
                  placeholder={LABEL.LOCATION}
                />
              </Form.Item>

              {/* [17] 数量 */}
              <Form.Item
                label={LABEL.QUANTITY}
                name={NAME.QUANTITY}
                validateStatus={getValidateStatus(
                  validationErrors,
                  NAME.QUANTITY
                )}
                help={validationErrors[NAME.QUANTITY]}
              >
                <Input size="large" maxLength={5} placeholder="例: 1" />
              </Form.Item>

              {/* [18] タグ要求 — numberless assets (no assetNo) have no reliable key to attribute
                  a seal request to, so the field is disabled rather than editable. */}
              <Form.Item
                label={LABEL.SEAL_REQUEST}
                name={NAME.SEAL_REQUEST}
                validateStatus={getValidateStatus(
                  validationErrors,
                  NAME.SEAL_REQUEST
                )}
                help={validationErrors[NAME.SEAL_REQUEST]}
              >
                <Input
                  size="large"
                  maxLength={3}
                  placeholder="例: 0"
                  disabled={!asset?.assetNo}
                />
              </Form.Item>

              {/* [19] 備考 */}
              <div className="detail_form-full">
                <Form.Item label={LABEL.REMARKS} name={NAME.REMARKS}>
                  <Input.TextArea
                    rows={3}
                    maxLength={200}
                    placeholder={LABEL.REMARKS}
                  />
                </Form.Item>
              </div>
            </div>
          </Form>
        </div>

        {/* [20-21] Status section */}
        <div className="detail_section">
          <div className="detail_section-title">{TITLE.STATUS_SECTION}</div>
          <div className="detail_status-row">
            {/* [20] 現在のステータス */}
            <div className="detail_status-item">
              <span className="detail_status-label">
                {LABEL.CURRENT_STATUS}:
              </span>
              {asset && (
                <StatusBadge status={asset.status as TAssetStatus}>
                  {asset.status}
                </StatusBadge>
              )}
            </div>

            {/* [21] 変更後ステータス */}
            <div className="detail_status-item">
              <span className="detail_status-label">
                {LABEL.TARGET_STATUS}:
              </span>
              <div className="detail_status-radio-group">
                <Radio.Group
                  value={targetStatus}
                  onChange={(e) => handler.setTargetStatus(e.target.value)}
                  disabled={isStatusLocked}
                >
                  {SELECTABLE_STATUSES.map((s) => (
                    <Radio key={s} value={s}>
                      {s}
                    </Radio>
                  ))}
                </Radio.Group>
                <span className="detail_status-note">
                  {isStatusLocked
                    ? LABEL.STATUS_LOCKED_NOTE
                    : LABEL.STATUS_NOTE}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* [22-23] Footer buttons */}
        <div className="detail_footer">
          <Button size="large" onClick={handler.handleBack}>
            {TITLE.BTN_BACK_FOOTER}
          </Button>
          <Button type="primary" size="large" onClick={handler.handleClickSave}>
            {TITLE.BTN_SAVE}
          </Button>
        </div>

        {/* VAMS_PC_005_01 Save Confirmation Dialog */}
        <SaveConfirmDialog
          open={showSaveDialog}
          loading={loadingSave}
          changedFields={handler.getChangedFields()}
          onCancel={() => handler.setShowSaveDialog(false)}
          onOk={handler.handleConfirmSave}
        />
      </DetailWrapper>
    </LoadingCommon>
  );
}

export { FixedAssetLedgerDetail };
